import React from "react";
import {
  PhoneOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import SearchBar from "../listingsfilter/SearchBar";

 export type SortOrder = "none" | "asc" | "desc";

interface DateRange {
  startDate: string;
  endDate: string;
}

 export const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 select-none">
    {children}
  </span>
);

export const DateInput: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value, onChange,
}) => (
  <input
    type="date"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="px-2.5 py-1.5 rounded-lg text-xs font-mono text-slate-700 border border-slate-200 bg-white
      focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all duration-150 cursor-pointer"
  />
);

export const PeriodPill: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({
  label, active, onClick,
}) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-150 cursor-pointer
      ${active
        ? "bg-[#0F52BA] text-white border-[#0F52BA] shadow-sm shadow-blue-200"
        : "bg-white text-slate-500 border-slate-200 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50"
      }`}
  >
    {label}
  </button>
);

export const SortCallsButton: React.FC<{ order: SortOrder; onChange: (o: SortOrder) => void }> = ({
  order, onChange,
}) => {
  const next: Record<SortOrder, SortOrder> = { none: "desc", desc: "asc", asc: "none" };
  const icon =
    order === "desc" ? <SortDescendingOutlined style={{ fontSize: 12 }} /> :
    order === "asc"  ? <SortAscendingOutlined  style={{ fontSize: 12 }} /> :
                       <SwapOutlined           style={{ fontSize: 12 }} />;
  const label = order === "desc" ? "Appels ↓" : order === "asc" ? "Appels ↑" : "Trier";

  return (
    <button
      onClick={() => onChange(next[order])}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-150 cursor-pointer
        ${order !== "none"
          ? "bg-blue-700 text-white border-blue-600 shadow-sm shadow-blue-200"
          : "bg-white text-slate-500 border-slate-200 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50"
        }`}
    >
      <PhoneOutlined style={{ fontSize: 11 }} />
      {icon}
      {label}
    </button>
  );
};

 interface ListingToolbarProps {
  search: string;
  onSearchChange: (v: string) => void;
  dateRange: DateRange;
  onDateChange: (field: "startDate" | "endDate", value: string) => void;
  activePeriod: number | null;
  onQuickRange: (days: number) => void;
  sortOrder: SortOrder;
  onSortChange: (o: SortOrder) => void;
}

const PERIODS = [7, 30, 90] as const;

const ListingToolbar: React.FC<ListingToolbarProps> = ({
  search, onSearchChange,
  dateRange, onDateChange,
  activePeriod, onQuickRange,
  sortOrder, onSortChange,
}) => (
  <div className="px-5 pt-5 pb-4 flex flex-wrap items-center gap-3 border-b border-slate-100">

     <div className="flex-1 min-w-[180px]">
      <SearchBar
        value={search}
        onChange={onSearchChange}
        placeholder="Rechercher par nom de fiche…"
      />
    </div>

     <SectionLabel>Période</SectionLabel>

     <div className="flex items-center gap-1.5">
      <DateInput
        value={dateRange.startDate}
        onChange={(v) => onDateChange("startDate", v)}
      />
      <span className="text-slate-300 text-sm select-none">–</span>
      <DateInput
        value={dateRange.endDate}
        onChange={(v) => onDateChange("endDate", v)}
      />
    </div>

     <div className="flex items-center gap-1">
      {PERIODS.map((d) => (
        <PeriodPill
          key={d}
          label={`${d}j`}
          active={activePeriod === d}
          onClick={() => onQuickRange(d)}
        />
      ))}
    </div>

     <div className="hidden sm:block h-4 w-px bg-slate-200" />

     <SortCallsButton order={sortOrder} onChange={onSortChange} />
  </div>
);

export default ListingToolbar;